import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * Suppress the built-in close button.
   *
   * The default X is `absolute` against the content box, so in a dialog that
   * scrolls as one piece it scrolls away with everything else. A consumer with a
   * pinned title bar should render its own X inside that bar — where it stays
   * reachable — and set this to avoid two of them.
   */
  hideClose?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Desktop modal (≥ lg = 1024px). Consumer-supplied `max-w-*` overrides
        // this default at lg+.
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg max-h-[90vh] overflow-y-auto',
        // Mobile/tablet (< lg): a centred card that sizes to its CONTENT, capped
        // so a long form fills the screen and scrolls internally while a
        // three-field dialog stays three fields tall. The old default forced
        // `h-[100dvh]` on every dialog, which padded short ones out to a screen
        // of empty space. Full-screen is now OPT-IN — see FULLSCREEN_DIALOG_CLASS
        // in dispatch/schedule.tsx, which restates its mobile sizing at this same
        // `max-lg:!` specificity so twMerge lets it win.
        //
        // The cap subtracts a 1rem gutter per side plus the iOS insets, so even a
        // maximally tall card clears the status bar / Dynamic Island and the home
        // indicator once it's centred.
        //
        // The `!` important forces these over consumer-supplied size utilities
        // (e.g. `max-w-3xl`) that have no breakpoint prefix and would otherwise
        // leak into the mobile layout.
        'max-lg:!w-[calc(100vw-2rem)] max-lg:!h-auto max-lg:!max-w-none',
        'max-lg:!max-h-[calc(100dvh-2rem-var(--safe-top)-var(--safe-bottom))]',
        'max-lg:!rounded-lg',
        // Anchored to the TOP below lg, not vertically centred. Centring means
        // `top: 50%` of `100dvh` — and the soft keyboard shrinks `dvh`, so a
        // centred dialog re-centres itself and lurches upward the moment the
        // keyboard arrives. Pinning the top edge means a viewport change only
        // shrinks the max-height above: the content scrolls, the dialog holds
        // still. At lg+ there's no keyboard shrinking anything, so it stays
        // centred there.
        'max-lg:!top-[calc(1rem_+_var(--safe-top))] max-lg:!translate-y-0 max-lg:!bottom-auto',
        // How much iOS inset this dialog has to reserve *itself*. Zero by
        // default: a centred card is held clear of the status bar / Dynamic
        // Island and the home indicator by the max-height cap above, so padding
        // it as well would just read as lopsided space at the top.
        //
        // Only an edge-to-edge dialog needs to reserve anything, and it opts in
        // by pointing this at the real insets (FULLSCREEN_DIALOG_CLASS does).
        // Routing through `--dialog-safe-*` instead of reading `--safe-*`
        // directly means one declaration moves both the padding and the close
        // button, which is absolutely positioned and so never moved by padding.
        '[--dialog-safe-top:0px] [--dialog-safe-bottom:0px]',
        // Ease the sheet's own resize. The keyboard opening or closing changes
        // `100dvh`, and the browser applies that as one hard reflow — the sheet's
        // bottom edge jumps, which is the part that reads as unsmooth on dismiss.
        // Transitioning height/max-height spreads it over the keyboard's own
        // travel instead.
        //
        // VABS solves the same jump in the native shell rather than the app
        // (`KeyboardResize.None` plus an animated iframe height) and does so on
        // iOS ONLY, on the grounds that a transition on Android "would just make
        // the iframe lag behind the OS resize". The same risk applies here: if
        // the sheet visibly trails the keyboard on Android, this line is the one
        // to drop.
        'max-lg:transition-[height,max-height] max-lg:duration-200 max-lg:ease-out',
        className
      )}
      {...props}
    >
      {children}
      {/* `top-4` is measured from the sheet's edge, which sits under the
          status bar on a full-screen mobile dialog — offset by the same
          inset the padding above uses so the X stays clear of the island.
          `--dialog-safe-top` is 0px at lg+ and for centred consumers.

          `z-20` because a consumer's sticky title bar carries `z-10` to cover
          the content scrolling beneath it — without this the X renders *under*
          that bar's background and simply isn't there. It's a later sibling in
          the DOM, which would normally be enough, but an explicit z-index beats
          source order.

          Note this is `absolute` against the content box, so in a dialog that
          scrolls as one piece it scrolls away with the content. A consumer whose
          dialog does that should pass `hideClose` and put its own X inside its
          pinned title bar — z-index cannot fix scrolling away. */}
      {!hideClose && (
        <DialogPrimitive.Close className="absolute right-4 top-[calc(1rem_+_var(--dialog-safe-top))] z-20 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

/**
 * Action bar.
 *
 * Deliberately NOT pinned on mobile. It used to be unshrinkable chrome at the
 * bottom of a `100dvh` sheet, which meant the soft keyboard and a stack of
 * full-width buttons fought over the same half of the screen — and every fix for
 * that (hide it while typing, collapse it with a transition) traded the problem
 * for a visible flicker as it came and went. Letting it scroll with the content
 * removes the conflict instead of timing it: the keyboard covers the bottom of a
 * scrollable page, which is what a keyboard normally does.
 *
 * Consumers that want the mobile full-screen layout hand scrolling to
 * DialogContent (`max-lg:flex-none max-lg:overflow-visible` on the body) so this
 * flows after the content. At `lg`+ it's a normal modal action row.
 */
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    // Stable hook for tests. The footer can't be found by role while a modal
    // Radix popover is open — those `aria-hidden` the rest of the page, so the
    // dialog and its buttons drop out of the accessibility tree entirely, and a
    // `getByRole` assertion about the footer would pass or fail for reasons that
    // have nothing to do with the footer. An attribute selector is unaffected.
    data-slot="dialog-footer"
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog, DialogPortal, DialogOverlay, DialogTrigger, DialogClose,
  DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
};
